import React, { useEffect, useMemo, useRef, useState } from "react";

export default function SearchBar(props) {
  const { fetchClients, clients, fetchClientAppts, clientAppts } = props;

  const [query, setQuery] = useState("");
  const [openResults, setOpenResults] = useState(false);
  const [clientPage, setClientPage] = useState(false);
  const [selectedClientLabel, setSelectedClientLabel] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  // NEW: loading state for search
  const [loading, setLoading] = useState(false);

  const rootRef = useRef(null);
  const debounceRef = useRef(null);

  // NEW: request id so old searches don’t overwrite new ones
  const reqIdRef = useRef(0);

  // click-outside close
  useEffect(() => {
    const handler = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setOpenResults(false);
        setLoading(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, []);

  // fetch clients (debounced) whenever query changes
  useEffect(() => {
    const q = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (q.length < 2) {
      setOpenResults(false);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      reqIdRef.current += 1;
      const myReq = reqIdRef.current;

      setLoading(true);
      setOpenResults(true);

      // If fetchClients returns a promise, stop loading on completion.
      // If it does not, we’ll stop loading when `clients` updates (effect below),
      // plus a safety timeout so it never spins forever.
      const maybePromise = fetchClients(q);

      if (maybePromise && typeof maybePromise.then === "function") {
        maybePromise.finally(() => {
          if (reqIdRef.current === myReq) setLoading(false);
        });
      } else {
        // safety timeout fallback
        window.setTimeout(() => {
          if (reqIdRef.current === myReq) setLoading(false);
        }, 1200);
      }
    }, 220);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, fetchClients]);

  // stop loading when clients updates (redux/state update path)
  useEffect(() => {
    if (!openResults) return;
    setLoading(false);
  }, [clients, openResults]);

  const normalized = (v) => String(v || "").toLowerCase();

  // local filtering (in case fetchClients returns a broad list)
  const filteredClients = useMemo(() => {
    const q = normalized(query).trim();
    if (!q || !clients) return [];

    return Object.keys(clients)
      .map((k) => clients[k])
      .filter((c) => {
        const first = normalized(c.firstName);
        const last = normalized(c.lastName);
        const email = normalized(c.email);
        const phone = normalized(c.phone);

        const full = `${first} ${last}`.trim();
        return (
          full.includes(q) ||
          first.includes(q) ||
          last.includes(q) ||
          email.includes(q) ||
          phone.replace(/\D/g, "").includes(q.replace(/\D/g, ""))
        );
      })
      .slice(0, 18);
  }, [clients, query]);

  const openClientPage = (client) => {
    if (!client) return;
    setClientPage(true);
    setSelectedClient(client);
    setSelectedClientLabel(`${client.firstName || ""} ${client.lastName || ""}`.trim());

    fetchClientAppts(client.firstName, client.lastName);

    // close dropdown
    setOpenResults(false);
    setLoading(false);
  };

  const closeAll = () => {
    setClientPage(false);
    setSelectedClient(null);
    setSelectedClientLabel(null);
    setOpenResults(false);
    setLoading(false);
    setQuery("");
  };

  return (
    <div ref={rootRef} className="sb-root">
      <div className="sb-input-wrap">
        <i className="fa-regular fa-magnifying-glass sb-icon" />
        <input
          className="sb-input"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          onFocus={() => {
            if (query.trim().length >= 2) setOpenResults(true);
          }}
          type="text"
          placeholder="Search clients (name, phone, email)…"
          autoComplete="off"
        />
        {query?.length > 0 ? (
          <button
            type="button"
            className="sb-clear"
            onClick={() => {
              setQuery("");
              setOpenResults(false);
              setLoading(false);
            }}
            aria-label="Clear search"
          >
            <i className="fa-regular fa-xmark" />
          </button>
        ) : null}
      </div>

      {/* Results dropdown */}
      {openResults ? (
        <div className="sb-results" role="listbox">
          {loading ? (
            <div className="sb-loading">
              <div className="sb-loading-dot" />
              <div className="sb-loading-dot" />
              <div className="sb-loading-dot" />
              <span>Searching…</span>
            </div>
          ) : null}

          {!loading && filteredClients.length === 0 ? (
            <div className="sb-empty">No matches.</div>
          ) : null}

          {!loading &&
            filteredClients.map((c) => {
              const label = `${c.firstName || ""} ${c.lastName || ""}`.trim();
              return (
                <div
                  key={c._id || `${label}-${c.email || ""}-${c.phone || ""}`}
                  className="sb-result"
                  onClick={() => openClientPage(c)}
                  role="option"
                >
                  <div className="sb-result-main">{label || "Unknown Client"}</div>
                  <div className="sb-result-sub">
                    {c.phone ? <span>{c.phone}</span> : null}
                    {c.phone && c.email ? <span className="sb-dot">•</span> : null}
                    {c.email ? <span>{c.email}</span> : null}
                  </div>
                </div>
              );
            })}
        </div>
      ) : null}

      {/* Client page drawer */}
      {clientPage && selectedClientLabel ? (
        <div className="sb-client-drawer">
          <div className="sb-client-top">
            <div className="sb-client-title">
              <div className="sb-client-name">{selectedClientLabel}</div>
              <div className="sb-client-meta">
                {selectedClient?.phone ? <span>{selectedClient.phone}</span> : null}
                {selectedClient?.phone && selectedClient?.email ? <span className="sb-dot">•</span> : null}
                {selectedClient?.email ? <span>{selectedClient.email}</span> : null}
              </div>
            </div>

            <button type="button" className="sb-close" onClick={closeAll}>
              CLOSE
            </button>
          </div>

          <div className="sb-client-body">
            {clientAppts ? (
              Object.keys(clientAppts).map((key) => {
                const a = clientAppts[key];
                return (
                  <div key={a._id || key} className="sb-appt-card">
                    <div className="sb-appt-row">
                      <div className="sb-appt-date">{a.date}</div>
                      {a.canceled ? <div className="sb-appt-cancel">Canceled</div> : null}
                    </div>

                    <div className="sb-appt-type">{a.type}</div>
                    <div className="sb-appt-time">{a.time}</div>

                    {a.notes ? <div className="sb-appt-notes">{a.notes}</div> : null}
                  </div>
                );
              })
            ) : (
              <div className="sb-empty">No appointments found.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
